import { useEffect, useMemo }                                    from 'react'
import { ThemeContext }                                           from './themeContext'
import { useThemeStore }                                          from './themeStore'
import { applyTheme, subscribeToSystemTheme, getGlassClasses }   from './themeUtils'
import { THEMES }                                                 from './tokens'

export function ThemeProvider({ children }) {
  const preference          = useThemeStore((s) => s.preference)
  const resolvedTheme       = useThemeStore((s) => s.resolvedTheme)
  const isTransitioning     = useThemeStore((s) => s.isTransitioning)
  const setTheme            = useThemeStore((s) => s.setTheme)
  const toggleTheme         = useThemeStore((s) => s.toggleTheme)
  const resetToSystem       = useThemeStore((s) => s.resetToSystem)
  const onSystemThemeChange = useThemeStore((s) => s.onSystemThemeChange)

  // Sync DOM (CSS vars + .dark class) on mount and on every resolved theme change.
  // The store calls applyTheme() synchronously inside setTheme(), so this useEffect
  // is primarily a safety net for the initial mount and SSR-style hydration gaps.
  useEffect(() => {
    applyTheme(resolvedTheme)
  }, [resolvedTheme])

  // Forward OS color-scheme changes into the store.
  // The store's onSystemThemeChange() only acts when preference === 'system',
  // so this subscription is harmless when a concrete theme is selected.
  useEffect(() => {
    return subscribeToSystemTheme(onSystemThemeChange)
  }, [onSystemThemeChange])

  // Memoised context value — prevents every consumer from re-rendering when
  // unrelated state changes. Only recalculates when the theme actually changes.
  const value = useMemo(() => ({
    preference,
    resolvedTheme,
    isTransitioning,

    isDark:   resolvedTheme === THEMES.DARK,
    isLight:  resolvedTheme === THEMES.LIGHT,
    isSystem: preference    === THEMES.SYSTEM,

    glassClasses: getGlassClasses(resolvedTheme),

    setTheme,
    toggleTheme,
    resetToSystem,
  }), [preference, resolvedTheme, isTransitioning, setTheme, toggleTheme, resetToSystem])

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}
